import React, { useState, useMemo, useEffect } from 'react';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { styles } from '../config/styles';
import {
  formatCurrency,
  formatTasaForDisplay,
  formatNumberForDisplay,
  formatComitenteForDisplay,
} from '../utils/formatters';
import { formatDate } from '../utils/dateHelpers';
import { compareValues } from '../utils/sortHelpers';
import { renderCommentCell } from '../utils/uiHelpers';
import { useHorizontalScrollSync } from '../hooks/useHorizontalScrollSync';
import { useStackedTable } from '../hooks/useStackedTable';
import MobileCollapsibleSection from './common/MobileCollapsibleSection';
import { useFechaHoy } from '../hooks/useFechaHoy';
import { useLicitacionFee } from '../hooks/useLicitacionFee';
import TopScrollbar from './TopScrollbar';
import { PencilIcon, TrashIcon, PlusIcon } from './Icons';
import { ROLES } from '../constants/roles';
import { formOptions } from '../constants/formOptions';
import { exportRowsToXlsx } from '../utils/excelExport';
import { buildFilaExport, ANCHOS_COLUMNAS_EXPORT } from '../utils/licitacionesExport';
import {
  calculateKpisLicitacion,
  calculateIngresoColocacion,
  calculateFeePorColocador,
  groupCajaPorComercialCliente,
  getLicitacionLock,
  montoEnMonedaIntegracion,
  requiereTipoCambio,
  getTipoCambio,
  monedaBucket,
} from '../utils/licitacionesCalculator';
import {
  softDeleteLicitacion,
  restaurarLicitacion,
  softDeleteOrden,
  setOrdenCargado,
  marcarCargadoGrupo,
  setLicitacionEstado,
  cerrarCarga,
  reabrirCarga,
} from '../services/licitacionesService';
import { subscribeByComitentes } from '../services/comitentesQueryService';
import { filterMisClientes } from '../utils/saldosScope';
import {
  colocadoresDeLicitacion,
  mercadoDeLicitacion,
  nombresColocadores,
  resolverColocadorOrden,
} from '../utils/colocadores';
import LicitacionFormModal from './licitaciones/LicitacionFormModal';
import LicitacionOrdenFormModal from './licitaciones/LicitacionOrdenFormModal';
import AdjudicacionModal from './licitaciones/AdjudicacionModal';
import ColocadoresAdminModal from './licitaciones/ColocadoresAdminModal';
import CajaSeguimientoPanel from './licitaciones/CajaSeguimientoPanel';
import ResumenPorTasa from './licitaciones/ResumenPorTasa';

const KpiCard = ({ title, value, warning }) => (
  <div className="axon-kpi-card" style={styles.kpiCard}>
    <h3 style={styles.kpiTitle}>{title}</h3>
    <p className="axon-kpi-value" style={styles.kpiValue}>{value}</p>
    {warning && (
      <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.7rem', fontWeight: 600, color: '#B45309' }}>
        {warning}
      </p>
    )}
  </div>
);

/**
 * Monto adjudicado en la moneda que integra el cliente. Las especies en USD que
 * se integran en pesos necesitan el TC de la especie: sin él se avisa en lugar
 * de mostrar dólares disfrazados de pesos.
 */
const MontoALiquidarCell = ({ orden, especie }) => {
  const montoEmision = parseFloat(orden.montoAdjudicado) || 0;
  if (!montoEmision) return '-';
  const { monto, sinTipoCambio } = montoEnMonedaIntegracion(montoEmision, especie, orden.monedaIntegracion);
  if (sinTipoCambio) {
    return (
      <span
        style={{ color: '#B45309', fontWeight: 600 }}
        title={`Adjudicado ${formatCurrency(montoEmision, 'USD')}. Cargá el tipo de cambio de la especie para ver los pesos a integrar.`}
      >
        Falta TC
      </span>
    );
  }
  return formatCurrency(monto, monedaBucket(orden.monedaIntegracion) === 'ARS' ? 'Pesos' : 'USD');
};

const estadoBadgeColors = {
  Abierta: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
  Cerrada: { backgroundColor: styles.colors.yellow, color: '#854D0E' },
  Adjudicada: { backgroundColor: styles.colors.green, color: '#065F46' },
  Liquidada: { backgroundColor: '#E5E7EB', color: '#374151' },
  Eliminada: { backgroundColor: styles.colors.red, color: '#991B1B' },
};

const LockBadge = ({ title }) => (
  <span title={title} style={{ marginLeft: '0.4rem', fontSize: '0.8rem', cursor: 'help' }}>🔒</span>
);

const EstadoBadge = ({ estado }) => (
  <span style={{
    ...(estadoBadgeColors[estado] || {}),
    padding: '0.2rem 0.6rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
  }}>
    {estado}
  </span>
);

const subTabContainerStyle = {
  display: 'flex',
  gap: '0.5rem',
  borderBottom: `1px solid ${styles.colors.gray}`,
  marginBottom: '1.5rem',
};

const subTabButtonStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontSize: '1rem',
  fontWeight: 600,
  padding: '0.5rem 1rem',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: styles.colors.mediumGray,
  borderBottom: '3px solid transparent',
  marginBottom: '-1px',
};

const subTabButtonActiveStyle = {
  color: styles.colors.primary,
  borderBottom: `3px solid ${styles.colors.primary}`,
};

const bannerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  padding: '0.85rem 1.25rem',
  marginBottom: '1.5rem',
  backgroundColor: styles.colors.red,
  border: '1px solid #FCA5A5',
  borderRadius: '0.5rem',
};

const infoBannerStyle = {
  ...bannerStyle,
  backgroundColor: styles.colors.yellow,
  border: '1px solid #FDE68A',
};

const filterBarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  marginBottom: '1rem',
  flexWrap: 'wrap',
};

const filterControlStyle = {
  padding: '0.45rem 0.7rem',
  border: '1px solid #E5E7EB',
  borderRadius: '0.5rem',
  fontSize: '0.85rem',
};

const seccionPasadasToggleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '100%',
  padding: '0.65rem 0.9rem',
  backgroundColor: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '0.5rem',
  cursor: 'pointer',
  fontFamily: "'Raleway', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 600,
  color: styles.colors.primary,
  textAlign: 'left',
};

const contadorPasadasStyle = {
  marginLeft: 'auto',
  padding: '0.15rem 0.55rem',
  borderRadius: '10px',
  backgroundColor: '#E5E7EB',
  color: '#374151',
  fontSize: '0.75rem',
  fontWeight: 600,
};

// Una licitación liquidada ya no necesita seguimiento: se archiva en la
// sección "Licitaciones pasadas" en vez de ocupar la lista principal.
const esLicitacionPasada = (licitacion) => licitacion.estado === 'Liquidada';

const describeIngresoColocacion = (fee, ingreso) => {
  if (fee.isLoading) return '...';
  if (fee.error) return 'No disponible';
  if (!(fee.feeBps > 0)) return 'Sin fee';
  return formatCurrency(ingreso.ars, 'Pesos') || '$ 0,00';
};

const describeAvisoColocacion = (fee, ingreso) => {
  if (!(fee.feeBps > 0)) return '';
  if (ingreso.ordenesSinTipoCambio > 0) {
    return `Falta TC: ${ingreso.ordenesSinTipoCambio} orden${ingreso.ordenesSinTipoCambio === 1 ? '' : 'es'} en USD sin calcular`;
  }
  return ingreso.ordenesConIngreso === 0 ? 'Se calcula sobre lo adjudicado' : '';
};

const plural = (n, singular, pluralTexto) => `${n} ${n === 1 ? singular : pluralTexto}`;

const formatBps = (bps) => `${formatNumberForDisplay(bps, bps % 1 ? 2 : 0)} bps`;

// Licitaciones con colocadores: el fee esperado es la suma del de cada colocador.
const describeFeePorColocador = (fee, calculo) => {
  if (fee.isLoading) return '...';
  if (fee.error) return 'No disponible';
  if (!calculo.algunFeePositivo) return calculo.feesCompletos ? 'Sin fee (0 bps)' : 'Sin fee cargado';
  return formatCurrency(calculo.ars, 'Pesos') || '$ 0,00';
};

const describeAvisoFeePorColocador = (fee, calculo) => {
  if (fee.isLoading || fee.error) return '';
  const avisos = [];
  const sinFee = calculo.porColocador.filter(fila => fila.feeBps === null).length;
  if (sinFee > 0) avisos.push(`Falta el fee de ${plural(sinFee, 'colocador', 'colocadores')}`);
  if (calculo.ordenesSinTipoCambio > 0) {
    avisos.push(`Falta TC: ${plural(calculo.ordenesSinTipoCambio, 'orden', 'órdenes')} en USD sin calcular`);
  }
  if (calculo.ordenesSinColocador > 0) {
    avisos.push(`${plural(calculo.ordenesSinColocador, 'orden adjudicada', 'órdenes adjudicadas')} sin colocador`);
  }
  return avisos.join(' · ');
};

const tituloFeePorColocador = (calculo) => {
  const bps = [...new Set(calculo.porColocador.map(fila => fila.feeBps))];
  return bps.length === 1 && bps[0] > 0 ? `Fee Esperado (${formatBps(bps[0])})` : 'Fee Esperado';
};

const feeColocadorPanelStyle = {
  marginTop: '0.75rem',
  border: '1px solid #E5E7EB',
  borderRadius: '0.5rem',
  backgroundColor: '#FFFFFF',
  overflowX: 'auto',
};

const feeColocadorCellStyle = {
  padding: '0.45rem 0.75rem',
  fontSize: '0.8rem',
  borderBottom: '1px solid #E5E7EB',
  textAlign: 'right',
  fontVariantNumeric: 'lining-nums tabular-nums',
  whiteSpace: 'nowrap',
};

/*
 * comitentesFilter (opcional, modo productor): lista de nroComitente. Las
 * licitaciones se leen completas (son datos de mercado, no de clientes), pero las
 * órdenes se suscriben acotadas a esos comitentes (única lectura que las reglas
 * le permiten a un productor) y se filtran por licitación en memoria. El
 * productor no puede crear ni administrar licitaciones.
 *
 * filtroMisClientes (opcional, rol Comercial): filtro de vista "Mis clientes" de
 * las órdenes (ver saldosScope.filterMisClientes).
 */
const LicitacionesTab = ({ userRole, userName, userId, clientesDb, datosFiscalesDb, onViewComment, comitentesFilter = null, filtroMisClientes = null }) => {
  const [licitaciones, setLicitaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLicitacionId, setSelectedLicitacionId] = useState(null);
  const [ordenes, setOrdenes] = useState([]);
  const [isLoadingOrdenes, setIsLoadingOrdenes] = useState(false);
  const [ordenesCaja, setOrdenesCaja] = useState([]);
  const [ordenesProductor, setOrdenesProductor] = useState([]);
  const esModoProductor = comitentesFilter !== null;
  const comitentesKey = esModoProductor ? [...comitentesFilter].sort().join('|') : null;
  const [activeSubTab, setActiveSubTab] = useState('ordenes');
  const [filterComercial, setFilterComercial] = useState('');
  const [searchText, setSearchText] = useState('');
  const tablaVigentes = useStackedTable({ title: 'Nombre', actions: 'Acciones' });
  const tablaPasadas = useStackedTable({ title: 'Nombre', actions: 'Acciones' });
  const tablaOrdenes = useStackedTable({
    title: 'Cliente',
    primary: ['Comitente', 'Tramo', 'Tasa / Precio', 'VN Solicitado', 'Cargada', 'Adjudicación', '$ a Liquidar'],
    actions: 'Acciones',
  });
  const [filterEstado, setFilterEstado] = useState('');
  const [verEliminadas, setVerEliminadas] = useState(false);
  const [verPasadas, setVerPasadas] = useState(false);
  const [eliminadas, setEliminadas] = useState([]);
  const [licitacionModal, setLicitacionModal] = useState({ open: false, editing: null });
  const [ordenModal, setOrdenModal] = useState({ open: false, editing: null, duplicateFrom: null });
  const [adjudicacionModal, setAdjudicacionModal] = useState({ open: false, especie: null });
  const [colocadoresModalOpen, setColocadoresModalOpen] = useState(false);

  const isComercial = userRole === ROLES.COMERCIAL;
  const isTrader = userRole === ROLES.TRADER;
  const isMiddleOffice = userRole === ROLES.MIDDLE_OFFICE;
  const isAdmin = userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN;
  const canManageLicitacion = isTrader || isMiddleOffice || isAdmin;
  const canCreateOrden = isComercial || isTrader || isMiddleOffice || isAdmin;
  // El catálogo de colocadores lo mantienen middle office y admin (mismo criterio que
  // gestionaColocadores() en las reglas).
  const canManageColocadores = isMiddleOffice || isAdmin;

  // El creador de una licitación puede editarla, eliminarla y adjudicarla
  // cualquiera sea el rol con el que entró en la sesión.
  const esCreador = (licitacion) => !!licitacion?.creadoPorId && licitacion.creadoPorId === userId;
  const canEditLicitacion = (licitacion) => canManageLicitacion || esCreador(licitacion);
  const canDeleteLicitacion = (licitacion) => canManageLicitacion || esCreador(licitacion);
  const canAdjudicar = (licitacion) => canManageLicitacion || esCreador(licitacion);

  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(db, 'licitaciones'), where('estado', '!=', 'Eliminada'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLicitaciones(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    }, (error) => {
      console.error('Error al obtener las licitaciones:', error);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Papelera: solo se suscribe cuando el usuario pide ver las eliminadas.
  useEffect(() => {
    if (!verEliminadas) { setEliminadas([]); return undefined; }
    const q = query(collection(db, 'licitaciones'), where('estado', '==', 'Eliminada'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setEliminadas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('Error al obtener las licitaciones eliminadas:', error);
    });
    return () => unsubscribe();
  }, [verEliminadas]);

  const selectedLicitacion = useMemo(
    () => licitaciones.find(l => l.id === selectedLicitacionId) || null,
    [licitaciones, selectedLicitacionId]
  );

  // Modo productor: una única suscripción a todas sus órdenes (acotada por
  // comitente); el detalle y el banner de caja se derivan de acá en memoria.
  useEffect(() => {
    if (comitentesKey === null) return undefined;
    const comitentes = comitentesKey ? comitentesKey.split('|') : [];
    return subscribeByComitentes('licitacionesOrdenes', comitentes, (docs) => {
      setOrdenesProductor(docs.filter(o => o.estado !== 'Eliminada'));
    });
  }, [comitentesKey]);

  // Órdenes de la licitación seleccionada (detalle).
  useEffect(() => {
    if (!selectedLicitacionId) { setOrdenes([]); return undefined; }
    if (esModoProductor) {
      setOrdenes(ordenesProductor.filter(o => o.licitacionId === selectedLicitacionId));
      setIsLoadingOrdenes(false);
      return undefined;
    }
    setIsLoadingOrdenes(true);
    const q = query(collection(db, 'licitacionesOrdenes'), where('licitacionId', '==', selectedLicitacionId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrdenes(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.estado !== 'Eliminada'));
      setIsLoadingOrdenes(false);
    }, (error) => {
      console.error('Error al obtener las órdenes de la licitación:', error);
      setIsLoadingOrdenes(false);
    });
    return () => unsubscribe();
  }, [selectedLicitacionId, esModoProductor, ordenesProductor]);

  // Licitaciones en período de caja (hoy >= fechaCaja, aún no liquidadas) para el banner.
  const hoyIso = useFechaHoy();
  const licitacionesEnCaja = useMemo(
    () => licitaciones.filter(l => l.estado !== 'Liquidada' && l.fechaCaja && l.fechaCaja <= hoyIso),
    [licitaciones, hoyIso]
  );

  const idsEnCaja = useMemo(
    // Firestore limita el operador 'in' a 10 valores; nunca debería haber más
    // de un puñado de licitaciones en día de caja a la vez.
    () => licitacionesEnCaja.map(l => l.id).slice(0, 10),
    [licitacionesEnCaja]
  );

  useEffect(() => {
    if (idsEnCaja.length === 0) { setOrdenesCaja([]); return undefined; }
    if (esModoProductor) {
      setOrdenesCaja(ordenesProductor.filter(o => idsEnCaja.includes(o.licitacionId)));
      return undefined;
    }
    const q = query(collection(db, 'licitacionesOrdenes'), where('licitacionId', 'in', idsEnCaja));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrdenesCaja(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.estado !== 'Eliminada'));
    }, (error) => {
      console.error('Error al obtener las órdenes para el banner de caja:', error);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsEnCaja.join(','), esModoProductor, ordenesProductor]);

  const bannerAlertas = useMemo(() => licitacionesEnCaja.map(licitacion => {
    const especiesById = {};
    (licitacion.especies || []).forEach(esp => { especiesById[esp.id] = esp; });
    const ordenesLic = ordenesCaja.filter(o => o.licitacionId === licitacion.id);
    const grupos = groupCajaPorComercialCliente(ordenesLic, especiesById).flatMap(s => s.grupos);
    const pendientes = grupos.filter(g => !g.cajaHecha).length;
    return { licitacion, pendientes, total: grupos.length };
  }).filter(alerta => alerta.pendientes > 0), [licitacionesEnCaja, ordenesCaja]);

  const sortedLicitaciones = useMemo(() => {
    const texto = searchText.trim().toLowerCase();
    return [...licitaciones, ...(verEliminadas ? eliminadas : [])]
      .filter(l => !filterEstado || l.estado === filterEstado)
      .filter(l => !texto
        || (l.nombre || '').toLowerCase().includes(texto)
        || (l.emisor || '').toLowerCase().includes(texto))
      .sort((a, b) => compareValues(a.fechaLicitacion, b.fechaLicitacion, 'descending'));
  }, [licitaciones, eliminadas, verEliminadas, filterEstado, searchText]);

  const licitacionesVigentes = useMemo(
    () => sortedLicitaciones.filter(l => !esLicitacionPasada(l)),
    [sortedLicitaciones]
  );

  const licitacionesPasadas = useMemo(
    () => sortedLicitaciones.filter(esLicitacionPasada),
    [sortedLicitaciones]
  );

  // Filtrar por "Liquidada" no mostraría nada si la sección archivada
  // quedara colapsada.
  useEffect(() => {
    if (filterEstado === 'Liquidada') setVerPasadas(true);
  }, [filterEstado]);

  const especiesById = useMemo(() => {
    const map = {};
    (selectedLicitacion?.especies || []).forEach(esp => { map[esp.id] = esp; });
    return map;
  }, [selectedLicitacion]);

  const uniqueComerciales = useMemo(() => {
    const values = new Set(ordenes.map(o => o.comercial).filter(Boolean));
    return [...values].sort();
  }, [ordenes]);

  // "Mis clientes" del comercial acota la tabla de órdenes y el seguimiento de caja;
  // los indicadores siguen siendo los de toda la licitación.
  const ordenesVisibles = useMemo(
    () => filterMisClientes(ordenes, filtroMisClientes),
    [ordenes, filtroMisClientes]
  );

  const ordenesFiltradas = useMemo(
    () => (filterComercial ? ordenesVisibles.filter(o => o.comercial === filterComercial) : ordenesVisibles),
    [ordenesVisibles, filterComercial]
  );

  const kpis = useMemo(
    () => calculateKpisLicitacion(ordenes, especiesById),
    [ordenes, especiesById]
  );

  // El fee de colocación es información interna: un productor no lo lee.
  const feeColocacion = useLicitacionFee(selectedLicitacionId, { enabled: !esModoProductor });
  const ingresoColocacion = useMemo(
    () => calculateIngresoColocacion(ordenes, especiesById, feeColocacion.feeBps),
    [ordenes, especiesById, feeColocacion.feeBps]
  );
  // Licitaciones con colocadores: fee de cada colocador sobre lo adjudicado de sus órdenes.
  const colocadoresSeleccionada = useMemo(() => colocadoresDeLicitacion(selectedLicitacion), [selectedLicitacion]);
  const conColocadores = colocadoresSeleccionada.length > 0;
  const feePorColocador = useMemo(
    () => calculateFeePorColocador(ordenes, especiesById, colocadoresSeleccionada, feeColocacion.feesPorColocador),
    [ordenes, especiesById, colocadoresSeleccionada, feeColocacion.feesPorColocador]
  );
  const mercadoSeleccionada = mercadoDeLicitacion(selectedLicitacion);
  // La tabla de órdenes suma la columna Colocador en las licitaciones con colocadores.
  const columnasOrdenes = conColocadores ? 16 : 15;

  const lock = useMemo(
    () => getLicitacionLock(selectedLicitacion || {}, hoyIso),
    [selectedLicitacion, hoyIso]
  );

  const especiesPendientes = useMemo(
    () => (selectedLicitacion?.especies || []).filter(esp => esp.estado === 'Pendiente'),
    [selectedLicitacion]
  );

  // Reabrir una licitación Adjudicada/Liquidada le haría perder ese estado.
  const puedeReabrirCarga = lock.cargaCerrada
    && (selectedLicitacion?.estado === 'Cerrada'
      || (selectedLicitacion?.estado === 'Abierta' && lock.vencidaPorFecha));

  const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
    useHorizontalScrollSync([ordenesFiltradas, isLoadingOrdenes, activeSubTab, selectedLicitacionId]);

  // Con la carga cerrada nadie edita órdenes: primero hay que reabrirla.
  // Con la caja ya marcada, la orden deja de ser del comercial (las reglas solo le
  // permiten tocar la marca de caja).
  const canEditOrden = (orden) => !lock.cargaCerrada
    && (canManageLicitacion
      || (isComercial && orden.creadoPorId === userId && !orden.cargado && !orden.cajaHecha));

  const handleSelectLicitacion = (id, subTab = 'ordenes') => {
    setSelectedLicitacionId(id);
    setActiveSubTab(subTab);
    setFilterComercial('');
  };

  /**
   * Elimina una licitación. Desde el detalle llegan las órdenes ya cargadas;
   * desde el listado se pasa null y el servicio las consulta.
   */
  const handleDeleteLicitacion = async (licitacion, ordenesCargadas = null) => {
    if (!licitacion) return;
    const detalleOrdenes = Array.isArray(ordenesCargadas) ? ` y sus ${ordenesCargadas.length} órdenes` : ' y sus órdenes';
    const confirmado = window.confirm(
      `¿Eliminar la licitación "${licitacion.nombre}"${detalleOrdenes}? Quedan archivadas como eliminadas y se pueden restaurar.`
    );
    if (!confirmado) return;
    try {
      await softDeleteLicitacion(licitacion, ordenesCargadas, { userId, userName });
      if (licitacion.id === selectedLicitacionId) setSelectedLicitacionId(null);
    } catch (error) {
      console.error('Error eliminando la licitación:', error);
      window.alert('No se pudo eliminar la licitación. Intentá de nuevo.');
    }
  };

  const handleRestaurarLicitacion = async (licitacion) => {
    const confirmado = window.confirm(
      `¿Restaurar la licitación "${licitacion.nombre}"? Vuelve como Cerrada, junto con las órdenes que se eliminaron con ella.`
    );
    if (!confirmado) return;
    try {
      await restaurarLicitacion(licitacion);
    } catch (error) {
      console.error('Error restaurando la licitación:', error);
      window.alert('No se pudo restaurar la licitación. Intentá de nuevo.');
    }
  };

  const handleDeleteOrden = async (orden) => {
    const confirmado = window.confirm(`¿Eliminar la orden de ${orden.cliente} (VN ${formatNumberForDisplay(orden.cantidadVN, 0)})?`);
    if (!confirmado) return;
    try {
      await softDeleteOrden(orden, { userId, userName });
    } catch (error) {
      console.error('Error eliminando la orden:', error);
      alert(error?.code === 'permission-denied'
        ? 'No se pudo eliminar la orden: ya la tomó la mesa o tu rol no lo permite.'
        : 'No se pudo eliminar la orden. Intentá de nuevo en un momento.');
    }
  };

  const handleToggleCargado = async (orden) => {
    try {
      await setOrdenCargado(orden, !orden.cargado, { userId, userName });
    } catch (error) {
      console.error('Error marcando la orden como cargada:', error);
    }
  };

  /**
   * Marca (o desmarca) como cargadas todas las órdenes de un tramo del resumen
   * por tasa. Opera sobre lo que muestra el resumen, así que con un filtro de
   * comercial activo solo toca las órdenes de ese comercial.
   */
  const handleMarcarCargadoGrupo = async (especie, grupo, cargado) => {
    const cantidad = cargado ? grupo.cantidadOrdenes - grupo.cantidadCargadas : grupo.cantidadCargadas;
    if (cantidad === 0) return;
    const tramo = grupo.esNoCompetitivo
      ? 'del tramo No Competitivo'
      : (especie.modalidad === 'Tasa'
        ? `al ${formatTasaForDisplay(grupo.valor)}`
        : `a precio ${formatNumberForDisplay(grupo.valor)}`);
    const ordenesTexto = cantidad === 1 ? 'la orden' : `las ${cantidad} órdenes`;
    const pregunta = cargado
      ? `¿Marcar como cargadas en el mercado ${ordenesTexto} ${tramo} de ${especie.denominacion}?`
      : `¿Desmarcar ${ordenesTexto} ${tramo} de ${especie.denominacion}? Vuelven a figurar como no cargadas.`;
    const aclaracionFiltro = filterComercial
      ? `\n\nHay un filtro de comercial activo: solo se ${cargado ? 'marcan' : 'desmarcan'} las de ${filterComercial}.`
      : '';
    if (!window.confirm(`${pregunta}${aclaracionFiltro}`)) return;
    try {
      await marcarCargadoGrupo(grupo.ordenes, cargado, { userId, userName });
    } catch (error) {
      console.error('Error marcando las órdenes del tramo como cargadas:', error);
      window.alert('No se pudieron actualizar las órdenes del tramo. Intentá de nuevo.');
    }
  };

  const handleEstadoLicitacion = async (estado) => {
    if (!selectedLicitacion) return;
    try {
      await setLicitacionEstado(selectedLicitacion.id, estado);
    } catch (error) {
      console.error('Error actualizando el estado de la licitación:', error);
    }
  };

  const handleCerrarCarga = async () => {
    if (!selectedLicitacion) return;
    try {
      await cerrarCarga(selectedLicitacion.id);
    } catch (error) {
      console.error('Error cerrando la carga de la licitación:', error);
    }
  };

  // Reabrir solo habilita la carga por el día de hoy: al día siguiente el
  // bloqueo automático vuelve a aplicar.
  const handleReabrirCarga = async () => {
    if (!selectedLicitacion) return;
    try {
      await reabrirCarga(selectedLicitacion.id, hoyIso, { userName });
    } catch (error) {
      console.error('Error reabriendo la carga de la licitación:', error);
    }
  };

  // Las primeras 19 columnas son las del Excel de carga del sistema; de la 20 en
  // adelante van los datos internos de seguimiento. Ver licitacionesExport.js.
  const handleExportOrdenes = () => {
    if (!selectedLicitacion) return;
    const filas = ordenesFiltradas.map(orden =>
      buildFilaExport(orden, especiesById[orden.especieId] || {}, selectedLicitacion));
    const exportado = exportRowsToXlsx(filas, {
      fileName: `Licitacion-${selectedLicitacion.nombre}-${selectedLicitacion.fechaLicitacion}`,
      sheetName: 'Órdenes',
      columnWidths: ANCHOS_COLUMNAS_EXPORT,
    });
    if (!exportado) window.alert('No hay órdenes para exportar.');
  };

  const renderBanner = () => bannerAlertas.length > 0 && (
    <div>
      {bannerAlertas.map(({ licitacion, pendientes, total }) => (
        <div key={licitacion.id} style={bannerStyle}>
          <div>
            <strong style={{ color: styles.colors.redText }}>
              {licitacion.fechaCaja === hoyIso ? '¡HOY es día de caja' : '¡Caja vencida'} de "{licitacion.nombre}"!
            </strong>
            <span style={{ marginLeft: '0.75rem', color: styles.colors.redText }}>
              {pendientes} de {total} clientes sin caja — liquida el {formatDate(`${licitacion.fechaLiquidacion}T00:00`)}
            </span>
          </div>
          <button
            style={{ ...styles.button, backgroundColor: '#DC2626' }}
            onClick={() => handleSelectLicitacion(licitacion.id, 'caja')}
          >
            Ver seguimiento
          </button>
        </div>
      ))}
    </div>
  );

  /**
   * Tabla de licitaciones, reutilizada por la lista principal (vigentes) y por
   * la sección de pasadas.
   */
  const renderTablaLicitaciones = (lista, mensajeVacio, stacked) => (
    <table ref={stacked.ref} className={stacked.className} style={{ ...styles.table, minWidth: '1150px' }}>
      <thead>
        <tr>
          <th style={styles.th}>Nombre</th>
          <th style={styles.th}>Emisor</th>
          <th style={styles.th}>Especies</th>
          <th style={styles.th}>Fecha Licitación</th>
          <th style={styles.th}>Fecha Caja</th>
          <th style={styles.th}>Fecha Liquidación</th>
          <th style={styles.th}>Creada por</th>
          <th style={styles.th}>Estado</th>
          <th style={styles.th}>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {lista.length === 0 && (
          <tr><td colSpan={9} style={{ ...styles.td, padding: '2rem', color: '#6B7281' }}>{mensajeVacio}</td></tr>
        )}
        {lista.map(licitacion => {
          const eliminada = licitacion.estado === 'Eliminada';
          const lockFila = getLicitacionLock(licitacion, hoyIso);
          return (
          <tr
            key={licitacion.id}
            style={{ cursor: eliminada ? 'default' : 'pointer', opacity: eliminada ? 0.55 : 1 }}
            onClick={() => !eliminada && handleSelectLicitacion(licitacion.id)}
          >
            <td style={{ ...styles.td, fontWeight: 600, textAlign: 'left' }}>{licitacion.nombre}</td>
            <td style={styles.td}>{licitacion.emisor || '-'}</td>
            <td style={styles.td}>
              {(licitacion.especies || []).map(esp => (
                <span key={esp.id} style={{
                  display: 'inline-block', margin: '0.1rem 0.2rem', padding: '0.15rem 0.5rem',
                  backgroundColor: '#EEF2FF', color: '#3730A3', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600,
                }}>
                  {esp.clase || esp.denominacion} · {esp.modalidad}
                </span>
              ))}
            </td>
            <td style={styles.td}>{formatDate(`${licitacion.fechaLicitacion}T00:00`)}</td>
            <td style={{
              ...styles.td,
              fontWeight: licitacion.fechaCaja === hoyIso ? 700 : 400,
              color: licitacion.fechaCaja === hoyIso ? styles.colors.redText : undefined,
            }}>
              {formatDate(`${licitacion.fechaCaja}T00:00`)}
            </td>
            <td style={styles.td}>{formatDate(`${licitacion.fechaLiquidacion}T00:00`)}</td>
            <td style={styles.td}>
              {eliminada
                ? `Eliminada por ${licitacion.eliminadoPorNombre || 's/d'}`
                : (licitacion.creadoPorNombre || '-')}
            </td>
            <td style={styles.td}>
              <EstadoBadge estado={licitacion.estado} />
              {!eliminada && lockFila.cargaCerrada && <LockBadge title={`Carga cerrada — ${lockFila.motivo}`} />}
            </td>
            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
              {eliminada ? (
                canDeleteLicitacion(licitacion) && (
                  <button
                    style={{ ...styles.button, padding: '0.35rem 0.75rem', fontSize: '0.8rem', backgroundColor: '#059669' }}
                    onClick={() => handleRestaurarLicitacion(licitacion)}
                  >
                    Restaurar
                  </button>
                )
              ) : (
                <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center', alignItems: 'center' }}>
                  <button style={{ ...styles.button, padding: '0.35rem 0.75rem', fontSize: '0.8rem' }} onClick={() => handleSelectLicitacion(licitacion.id)}>
                    Ver detalle
                  </button>
                  {canDeleteLicitacion(licitacion) && (
                    <button
                      style={styles.iconButton}
                      title="Eliminar licitación"
                      onClick={() => handleDeleteLicitacion(licitacion)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              )}
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );

  // ---------- Vista master: lista de licitaciones ----------
  if (!selectedLicitacion) {
    return (
      <div>
        {renderBanner()}
        <div className="axon-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '0.75rem' }}>
          <h2 style={{ fontFamily: "'Raleway', sans-serif", color: styles.colors.primary, margin: 0 }}>Licitaciones</h2>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {canManageColocadores && (
              <button
                style={{ ...styles.button, backgroundColor: '#FFFFFF', color: styles.colors.primary, border: `1px solid ${styles.colors.gray}` }}
                onClick={() => setColocadoresModalOpen(true)}
                title="Catálogo de colocadores de licitaciones"
              >
                Colocadores
              </button>
            )}
            {canManageLicitacion && (
              <button className="axon-fab" style={styles.button} onClick={() => setLicitacionModal({ open: true, editing: null })}>
                <PlusIcon /> Nueva Licitación
              </button>
            )}
          </div>
        </div>

        <div style={filterBarStyle}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Buscar por nombre o emisor..."
            className="axon-fill"
            style={{ ...filterControlStyle, minWidth: '260px' }}
          />
          <select value={filterEstado} onChange={(e) => setFilterEstado(e.target.value)} style={filterControlStyle}>
            <option value="">Todos los estados</option>
            {formOptions.estadoLicitacion.map(estado => <option key={estado} value={estado}>{estado}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer', color: styles.colors.mediumGray }}>
            <input type="checkbox" checked={verEliminadas} onChange={(e) => setVerEliminadas(e.target.checked)} />
            Ver eliminadas
          </label>
          <span style={{ fontSize: '0.8rem', color: '#465163', marginLeft: 'auto' }}>
            {licitacionesVigentes.length} {licitacionesVigentes.length === 1 ? 'licitación' : 'licitaciones'}
            {licitacionesPasadas.length > 0 && ` · ${licitacionesPasadas.length} pasada${licitacionesPasadas.length === 1 ? '' : 's'}`}
          </span>
        </div>

        <div style={styles.tableContainer}>
          {isLoading
            ? <p style={{ textAlign: 'center', padding: '2rem' }}>Cargando licitaciones...</p>
            : renderTablaLicitaciones(licitacionesVigentes, licitaciones.length === 0
              ? `No hay licitaciones cargadas. ${canManageLicitacion ? 'Creá la primera con "Nueva Licitación".' : ''}`
              : 'Ninguna licitación vigente coincide con el filtro.', tablaVigentes)}
        </div>

        {!isLoading && licitacionesPasadas.length > 0 && (
          <div style={{ marginTop: '1.75rem' }}>
            <button
              type="button"
              onClick={() => setVerPasadas(prev => !prev)}
              aria-expanded={verPasadas}
              style={seccionPasadasToggleStyle}
            >
              <span style={{ fontSize: '0.7rem' }}>{verPasadas ? '▾' : '▸'}</span>
              Licitaciones pasadas
              <span style={contadorPasadasStyle}>{licitacionesPasadas.length}</span>
            </button>
            {verPasadas && (
              <div style={{ ...styles.tableContainer, marginTop: '0.75rem' }}>
                {renderTablaLicitaciones(licitacionesPasadas, 'Ninguna licitación pasada coincide con el filtro.', tablaPasadas)}
              </div>
            )}
          </div>
        )}

        <LicitacionFormModal
          isOpen={licitacionModal.open}
          onClose={() => setLicitacionModal({ open: false, editing: null })}
          editingLicitacion={licitacionModal.editing}
          ordenes={[]}
          userName={userName}
          userId={userId}
        />
        <ColocadoresAdminModal isOpen={colocadoresModalOpen} onClose={() => setColocadoresModalOpen(false)} />
      </div>
    );
  }

  // ---------- Vista detalle de una licitación ----------
  return (
    <div>
      {renderBanner()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <button
            onClick={() => setSelectedLicitacionId(null)}
            style={{ background: 'none', border: 'none', color: styles.colors.accent, cursor: 'pointer', padding: 0, fontSize: '0.85rem', marginBottom: '0.35rem' }}
          >
            ← Volver a Licitaciones
          </button>
          <h2 style={{ fontFamily: "'Raleway', sans-serif", color: styles.colors.primary, margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {selectedLicitacion.nombre}
            <EstadoBadge estado={selectedLicitacion.estado} />
          </h2>
          <p style={{ margin: '0.35rem 0 0 0', fontSize: '0.85rem', color: '#6B7281' }}>
            {selectedLicitacion.emisor && `${selectedLicitacion.emisor} · `}
            Licita: {formatDate(`${selectedLicitacion.fechaLicitacion}T00:00`)} ·
            Caja: <strong>{formatDate(`${selectedLicitacion.fechaCaja}T00:00`)}</strong> ·
            Liquida: {formatDate(`${selectedLicitacion.fechaLiquidacion}T00:00`)}
            {conColocadores && ` · ${colocadoresSeleccionada.length === 1 ? 'Colocador' : 'Colocadores'}: ${nombresColocadores(colocadoresSeleccionada)}`}
            {mercadoSeleccionada && ` · Mercado: ${mercadoSeleccionada}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {canManageLicitacion && (
            <>
              {!lock.cargaCerrada && (
                <button style={styles.button} onClick={handleCerrarCarga}>Cerrar carga</button>
              )}
              {puedeReabrirCarga && (
                <button style={{ ...styles.button, backgroundColor: '#6B7281' }} onClick={handleReabrirCarga}>
                  {lock.vencidaPorFecha ? 'Reabrir carga por hoy' : 'Reabrir carga'}
                </button>
              )}
              {selectedLicitacion.estado === 'Adjudicada' && (
                <button style={styles.button} onClick={() => handleEstadoLicitacion('Liquidada')}>Marcar Liquidada</button>
              )}
            </>
          )}
          {canEditLicitacion(selectedLicitacion) && (
            <button
              style={{ ...styles.button, backgroundColor: '#FFFFFF', color: styles.colors.primary, border: `1px solid ${styles.colors.gray}` }}
              onClick={() => setLicitacionModal({ open: true, editing: selectedLicitacion })}
            >
              <PencilIcon /> Editar
            </button>
          )}
          {canDeleteLicitacion(selectedLicitacion) && (
            <button
              style={{ ...styles.button, backgroundColor: '#DC2626' }}
              onClick={() => handleDeleteLicitacion(selectedLicitacion, ordenes)}
            >
              <TrashIcon /> Eliminar
            </button>
          )}
        </div>
      </div>

      {lock.cargaCerrada && (
        <div style={{ ...infoBannerStyle, marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#854D0E' }}>
            <strong>Carga de órdenes cerrada</strong> — {lock.motivo}
            {lock.vencidaPorFecha && ` (licitó el ${formatDate(`${selectedLicitacion.fechaLicitacion}T00:00`)})`}.
            {' '}Ya no se pueden agregar, editar ni eliminar órdenes.
            {canManageLicitacion && puedeReabrirCarga && ' Si necesitás corregir algo, reabrí la carga.'}
          </div>
        </div>
      )}

      {lock.reaperturaVigente && (
        <div style={{ ...infoBannerStyle, marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#854D0E' }}>
            <strong>Carga reabierta hasta el {formatDate(`${selectedLicitacion.cargaReabiertaHasta}T00:00`)}</strong>
            {selectedLicitacion.cargaReabiertaPorNombre && ` por ${selectedLicitacion.cargaReabiertaPorNombre}`}
            {' '}— la licitación ya se realizó, revisá lo que cargues.
          </div>
        </div>
      )}

      {lock.vencidaPorFecha && especiesPendientes.length > 0
        && selectedLicitacion.estado !== 'Liquidada' && canAdjudicar(selectedLicitacion) && (
        <div style={{ ...bannerStyle, marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.85rem', color: styles.colors.redText }}>
            <strong>La licitación ya se realizó — falta cargar lo adjudicado</strong>
            <span style={{ marginLeft: '0.6rem' }}>
              {especiesPendientes.length === 1
                ? `Especie sin adjudicar: ${especiesPendientes[0].clase || especiesPendientes[0].denominacion}`
                : `${especiesPendientes.length} especies sin adjudicar`}
            </span>
          </div>
          <button
            style={{ ...styles.button, backgroundColor: '#DC2626' }}
            onClick={() => { setActiveSubTab('ordenes'); setAdjudicacionModal({ open: true, especie: especiesPendientes[0] }); }}
          >
            Cargar adjudicado
          </button>
        </div>
      )}

      <MobileCollapsibleSection
        title="Indicadores"
        summary={`${kpis.ordenesCargadas} / ${kpis.totalOrdenes} cargadas · ${formatNumberForDisplay(kpis.pctAdjudicado * 100, 1)}% adj.`}
      >
      <div className="axon-grid-2" style={styles.kpiContainer}>
        <KpiCard title="VN Solicitado" value={formatNumberForDisplay(kpis.vnSolicitado, 0) || '0'} />
        <KpiCard title="VN Adjudicado" value={formatNumberForDisplay(kpis.vnAdjudicado, 0) || '0'} />
        <KpiCard title="% Adjudicado" value={`${formatNumberForDisplay(kpis.pctAdjudicado * 100, 1)}%`} />
        <KpiCard
          title="A Liquidar ARS"
          value={formatCurrency(kpis.montoARS, 'Pesos') || '$ 0,00'}
          warning={kpis.sinTipoCambio ? 'Incompleto: falta tipo de cambio' : ''}
        />
        <KpiCard title="A Liquidar USD" value={formatCurrency(kpis.montoUSD, 'USD') || 'U$S 0,00'} />
        <KpiCard title="Órdenes Cargadas" value={`${kpis.ordenesCargadas} / ${kpis.totalOrdenes}`} />
        <KpiCard title="Caja Hecha" value={`${kpis.cajaHecha} / ${kpis.cajaTotal}`} />
        {!esModoProductor && conColocadores && (
          <KpiCard
            title={tituloFeePorColocador(feePorColocador)}
            value={describeFeePorColocador(feeColocacion, feePorColocador)}
            warning={describeAvisoFeePorColocador(feeColocacion, feePorColocador)}
          />
        )}
        {!esModoProductor && !conColocadores && (
          <KpiCard
            title={feeColocacion.feeBps > 0
              ? `Fee Esperado (${formatBps(feeColocacion.feeBps)})`
              : 'Fee Esperado'}
            value={describeIngresoColocacion(feeColocacion, ingresoColocacion)}
            warning={describeAvisoColocacion(feeColocacion, ingresoColocacion)}
          />
        )}
      </div>
      {!esModoProductor && conColocadores && !feeColocacion.isLoading && !feeColocacion.error && (
        <div style={feeColocadorPanelStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Fee esperado por colocador">
            <thead>
              <tr>
                <th style={{ ...feeColocadorCellStyle, textAlign: 'left', fontWeight: 600 }}>Colocador</th>
                <th style={{ ...feeColocadorCellStyle, fontWeight: 600 }}>Fee</th>
                <th style={{ ...feeColocadorCellStyle, fontWeight: 600 }}>Adjudicado (ARS)</th>
                <th style={{ ...feeColocadorCellStyle, fontWeight: 600 }}>Fee esperado</th>
              </tr>
            </thead>
            <tbody>
              {feePorColocador.porColocador.map(fila => (
                <tr key={fila.id}>
                  <td style={{ ...feeColocadorCellStyle, textAlign: 'left' }}>{fila.nombre}</td>
                  <td style={{ ...feeColocadorCellStyle, color: fila.feeBps === null ? '#946010' : undefined }}>
                    {fila.feeBps === null ? 'Sin cargar' : formatBps(fila.feeBps)}
                  </td>
                  <td style={feeColocadorCellStyle}>{formatCurrency(fila.montoArs, 'Pesos') || '$ 0,00'}</td>
                  <td style={feeColocadorCellStyle}>{formatCurrency(fila.ars, 'Pesos') || '$ 0,00'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </MobileCollapsibleSection>

      <div className="axon-scroll-x" style={subTabContainerStyle}>
        <button
          style={activeSubTab === 'ordenes' ? { ...subTabButtonStyle, ...subTabButtonActiveStyle } : subTabButtonStyle}
          onClick={() => setActiveSubTab('ordenes')}
        >
          Órdenes
        </button>
        <button
          style={activeSubTab === 'caja' ? { ...subTabButtonStyle, ...subTabButtonActiveStyle } : subTabButtonStyle}
          onClick={() => setActiveSubTab('caja')}
        >
          Seguimiento de Caja
        </button>
      </div>

      {activeSubTab === 'caja' && (
        <CajaSeguimientoPanel
          licitacion={selectedLicitacion}
          ordenes={ordenesVisibles}
          userRole={userRole}
          userName={userName}
          userId={userId}
        />
      )}

      {activeSubTab === 'ordenes' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: styles.colors.mediumGray, textTransform: 'uppercase' }}>Comercial:</label>
              <select
                value={filterComercial}
                onChange={(e) => setFilterComercial(e.target.value)}
                style={{ padding: '0.4rem 0.65rem', border: '1px solid #E5E7EB', borderRadius: '0.5rem', fontSize: '0.8rem' }}
              >
                <option value="">Todos</option>
                {uniqueComerciales.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                style={{ ...styles.button, backgroundColor: '#FFFFFF', color: styles.colors.primary, border: `1px solid ${styles.colors.gray}` }}
                onClick={handleExportOrdenes}
                disabled={ordenesFiltradas.length === 0}
                title="Descargar las órdenes con lo adjudicado"
              >
                Exportar a Excel
              </button>
              {canCreateOrden && !lock.cargaCerrada && (
                <button className="axon-fab" style={styles.button} onClick={() => setOrdenModal({ open: true, editing: null, duplicateFrom: null })}>
                  <PlusIcon /> Nueva Orden
                </button>
              )}
            </div>
          </div>

          <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />
          <div style={styles.tableContainer} ref={contentRef} onScroll={onContentScroll}>
            {isLoadingOrdenes ? <p style={{ textAlign: 'center', padding: '2rem' }}>Cargando órdenes...</p> : (
              <table ref={tablaOrdenes.ref} className={tablaOrdenes.className} style={{ ...styles.table, minWidth: '1400px' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Comitente</th>
                    <th style={styles.th}>Cliente</th>
                    <th style={styles.th}>CUIT</th>
                    <th style={styles.th}>Comercial</th>
                    {conColocadores && <th style={styles.th}>Colocador</th>}
                    <th style={styles.th}>Tramo</th>
                    <th style={styles.th}>Tasa / Precio</th>
                    <th style={styles.th}>VN Solicitado</th>
                    <th style={styles.th}>Moneda</th>
                    <th style={styles.th}>Cargada</th>
                    <th style={styles.th}>Adjudicación</th>
                    <th style={styles.th}>VN Adj.</th>
                    <th style={styles.th}>% Adj.</th>
                    <th style={styles.th}>$ a Liquidar</th>
                    <th style={styles.th}>Obs</th>
                    <th style={styles.th}>Acciones</th>
                  </tr>
                </thead>
                {(selectedLicitacion.especies || []).map(especie => {
                  const ordenesEspecie = ordenesFiltradas.filter(o => o.especieId === especie.id);
                  const vnSolicitadoEspecie = ordenesEspecie.reduce((acc, o) => acc + (parseFloat(o.cantidadVN) || 0), 0);
                  const vnAdjudicadoEspecie = ordenesEspecie.reduce((acc, o) => acc + (parseFloat(o.vnAdjudicado) || 0), 0);
                  const pctEspecie = vnSolicitadoEspecie > 0 ? vnAdjudicadoEspecie / vnSolicitadoEspecie * 100 : 0;
                  return (
                    <tbody key={especie.id}>
                      <tr>
                        <td colSpan={columnasOrdenes} style={{
                          ...styles.td, textAlign: 'left', backgroundColor: styles.colors.primary,
                          color: '#FFFFFF', fontWeight: 600, padding: '0.6rem 1rem',
                        }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            {especie.denominacion}{especie.clase ? ` (${especie.clase})` : ''} — licita {especie.modalidad}
                            <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>
                              {especie.estado === 'Pendiente' && 'Sin adjudicar'}
                              {/* Adjudicada a mano puede no tener corte: mostrar "Corte: 0" confundiría. */}
                              {especie.estado === 'Adjudicada' && (especie.tasaCorte > 0 || especie.precioCorte > 0
                                ? `Corte: ${especie.modalidad === 'Tasa'
                                  ? formatTasaForDisplay(especie.tasaCorte)
                                  : formatNumberForDisplay(especie.precioCorte)}${especie.modalidad === 'Tasa' && especie.precioCorte > 0 ? ` · Px ${formatNumberForDisplay(especie.precioCorte)}` : ''}`
                                : 'Sin corte informado')}
                              {especie.estado === 'Desierta' && 'Desierta'}
                            </span>
                            {vnSolicitadoEspecie > 0 && (
                              <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>
                                Adjudicado: {formatNumberForDisplay(vnAdjudicadoEspecie, 0)} / {formatNumberForDisplay(vnSolicitadoEspecie, 0)}
                                {' '}({formatNumberForDisplay(pctEspecie, 1)}%)
                              </span>
                            )}
                            {requiereTipoCambio(especie) && (
                              getTipoCambio(especie) > 0 ? (
                                <span
                                  style={{ fontWeight: 400, fontSize: '0.8rem' }}
                                  title="Tipo de cambio usado para convertir el precio en USD a los pesos que integra el cliente"
                                >
                                  TC {formatNumberForDisplay(getTipoCambio(especie))}
                                </span>
                              ) : (
                                <span
                                  style={{ fontWeight: 700, fontSize: '0.8rem', color: '#FCD34D' }}
                                  title="Se licita precio en USD y se integra en pesos: editá la licitación y cargá el tipo de cambio (Com. A 3500) para que los montos en pesos sean correctos"
                                >
                                  ⚠ Falta tipo de cambio
                                </span>
                              )
                            )}
                            {especie.adjudicadoPorNombre && (
                              <span
                                style={{ fontWeight: 400, fontSize: '0.75rem', opacity: 0.8 }}
                                title={`Modo de carga: ${especie.adjudicadoModo || 's/d'}`}
                              >
                                Adjudicada por {especie.adjudicadoPorNombre}
                                {especie.adjudicadoAt && ` el ${formatDate(especie.adjudicadoAt)}`}
                              </span>
                            )}
                            {canAdjudicar(selectedLicitacion) && selectedLicitacion.estado !== 'Liquidada' && ordenesEspecie.length > 0 && (
                              <button
                                style={{ ...styles.button, backgroundColor: '#FFFFFF', color: styles.colors.primary, padding: '0.25rem 0.7rem', fontSize: '0.75rem' }}
                                onClick={() => setAdjudicacionModal({ open: true, especie })}
                              >
                                {especie.estado === 'Pendiente' ? 'Adjudicar' : 'Re-adjudicar'}
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                      <ResumenPorTasa
                        especie={especie}
                        ordenes={ordenesEspecie}
                        colSpan={columnasOrdenes}
                        canMarcarCargado={canManageLicitacion}
                        onMarcarCargado={(grupo, cargado) => handleMarcarCargadoGrupo(especie, grupo, cargado)}
                      />
                      {ordenesEspecie.length === 0 && (
                        <tr><td colSpan={columnasOrdenes} style={{ ...styles.td, color: '#465163' }}>Sin órdenes para esta especie.</td></tr>
                      )}
                      {ordenesEspecie.map(orden => {
                        const rowColor = orden.estadoAdjudicacion === 'Adjudicada'
                          ? styles.colors.green
                          : (orden.estadoAdjudicacion === 'Rechazada' ? styles.colors.red : 'transparent');
                        return (
                          <tr key={orden.id} style={{ backgroundColor: rowColor }}>
                            <td style={styles.td}>{formatComitenteForDisplay(orden.nroComitente)}</td>
                            <td style={{ ...styles.td, textAlign: 'left' }}>{orden.cliente}</td>
                            <td style={styles.td}>{orden.cuit || '-'}</td>
                            <td style={styles.td}>{orden.comercial}</td>
                            {conColocadores && (
                              <td style={styles.td}>
                                {resolverColocadorOrden(orden, colocadoresSeleccionada)?.nombre || (
                                  <span style={{ color: '#946010', fontWeight: 600 }} title="Editá la orden y elegí por cuál colocador va">
                                    Sin asignar
                                  </span>
                                )}
                              </td>
                            )}
                            <td style={styles.td}>{orden.tramo === 'No Competitivo' ? 'NC' : 'Comp.'}</td>
                            <td style={styles.td}>
                              {orden.tramo === 'No Competitivo'
                                ? '-'
                                : (especie.modalidad === 'Tasa'
                                  ? formatTasaForDisplay(orden.tasaOfertada)
                                  : formatNumberForDisplay(orden.precioOfertado))}
                            </td>
                            <td style={styles.td}>{formatNumberForDisplay(orden.cantidadVN, 0)}</td>
                            <td style={styles.td}>{orden.monedaIntegracion}</td>
                            <td style={styles.td}>
                              <input
                                type="checkbox"
                                checked={orden.cargado === true}
                                onChange={() => handleToggleCargado(orden)}
                                disabled={!canManageLicitacion}
                                style={{ cursor: canManageLicitacion ? 'pointer' : 'not-allowed' }}
                                title={orden.cargado && orden.cargadoPorNombre ? `Cargada por ${orden.cargadoPorNombre}` : 'Orden cargada en el mercado'}
                              />
                            </td>
                            <td style={styles.td}>{orden.estadoAdjudicacion}</td>
                            <td style={styles.td}>{orden.vnAdjudicado ? formatNumberForDisplay(orden.vnAdjudicado, 0) : '-'}</td>
                            <td style={{ ...styles.td, fontWeight: 600 }}>
                              {orden.vnAdjudicado && orden.cantidadVN
                                ? `${formatNumberForDisplay(orden.vnAdjudicado / orden.cantidadVN * 100, 1)}%`
                                : '-'}
                            </td>
                            <td style={styles.td}>
                              <MontoALiquidarCell orden={orden} especie={especie} />
                            </td>
                            <td style={styles.td}>{renderCommentCell(orden.obs, `Obs — ${orden.cliente}`, onViewComment)}</td>
                            <td style={styles.td}>
                              {canEditOrden(orden) && (
                                <>
                                  <button
                                    style={styles.iconButton}
                                    title="Editar orden"
                                    onClick={() => setOrdenModal({ open: true, editing: orden, duplicateFrom: null })}
                                  >
                                    <PencilIcon />
                                  </button>
                                  {canCreateOrden && !lock.cargaCerrada && (
                                    <button
                                      style={{ ...styles.iconButton, fontWeight: 700, fontSize: '0.9rem' }}
                                      title="Duplicar orden (mismos datos, otro cliente)"
                                      onClick={() => setOrdenModal({ open: true, editing: null, duplicateFrom: orden })}
                                    >
                                      ⧉
                                    </button>
                                  )}
                                  <button style={styles.iconButton} title="Eliminar orden" onClick={() => handleDeleteOrden(orden)}>
                                    <TrashIcon />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  );
                })}
              </table>
            )}
          </div>
        </>
      )}

      <LicitacionFormModal
        isOpen={licitacionModal.open}
        onClose={() => setLicitacionModal({ open: false, editing: null })}
        editingLicitacion={licitacionModal.editing}
        ordenes={ordenes}
        userName={userName}
        userId={userId}
      />
      <LicitacionOrdenFormModal
        isOpen={ordenModal.open}
        onClose={() => setOrdenModal({ open: false, editing: null, duplicateFrom: null })}
        licitacion={selectedLicitacion}
        editingOrden={ordenModal.editing}
        duplicateFrom={ordenModal.duplicateFrom}
        clientesDb={clientesDb}
        datosFiscalesDb={datosFiscalesDb}
        cargaCerrada={lock.cargaCerrada}
        userRole={userRole}
        userName={userName}
        userId={userId}
      />
      <AdjudicacionModal
        isOpen={adjudicacionModal.open}
        onClose={() => setAdjudicacionModal({ open: false, especie: null })}
        licitacion={selectedLicitacion}
        especie={adjudicacionModal.especie}
        ordenes={ordenes}
        userId={userId}
        userName={userName}
      />
    </div>
  );
};

export default LicitacionesTab;
